// Browser-side Razorpay helpers shared by every payment surface (MP1, MP2,
// Cart Checkout). No checkout-specific logic lives here.

export type PaymentContext = {
  order_ref: string;
  customer_name: string;
  phone: string;
  email: string | null;
  meal_plan_title: string;
  quantity: number;
  amount_paise: number;
  currency: string;
  payment_status: string;
  /** Saved server-side environment for this order ("test" | "live"). */
  payment_environment?: string;
};

export type PaymentPhase = "loading" | "ready" | "paying" | "success" | "error";

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (payload: unknown) => void) => void;
    };
  }
}

let scriptLoaded = false;

/** Injects the Razorpay Checkout script once per document. */
export async function loadRazorpayScript(): Promise<boolean> {
  if (scriptLoaded && window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => {
      scriptLoaded = true;
      resolve(true);
    };
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/** Maps `/api/public/payments/begin` failure reasons to customer copy. */
export function friendlyBeginReason(r?: string): string {
  switch (r) {
    case "razorpay_unavailable":
      return "Our payments provider is temporarily unavailable. Please try again in a minute.";
    case "authorized_pending_capture":
      return "A previous payment for this order is still being confirmed. Please wait a moment and refresh.";
    case "in_progress_try_again":
      return "Another payment attempt is being prepared. Please try again in a few seconds.";
    case "expired":
    case "no_session":
      return "Your payment session has expired. Please open the payment link again.";
    default:
      return "Payment could not be started. Your order is safe and no money was charged.";
  }
}

export function formatInr(amountPaise?: number | null): string {
  return amountPaise ? (amountPaise / 100).toFixed(2) : "—";
}